import db from "./offlineDb";
import { getOfflineContext, setOfflineTermSession, updateOfflineContextFromAuth } from "./offlineContext";
import { getApiBaseUrl } from "../helpers/apiUrl";

const BASE_URL = getApiBaseUrl();
const PRELOAD_TTL_MS = 5 * 60 * 1000;
const pendingPreloads = new Map();

function getToken() {
  try {
    return JSON.parse(localStorage.getItem("access_token")) || null;
  } catch {
    return localStorage.getItem("access_token") || null;
  }
}

function schedule(task) {
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => task(), { timeout: 1500 });
  } else {
    window.setTimeout(task, 250);
  }
}

async function upsertPreloadStatus(ctx, classroomId, patch = {}) {
  if (!ctx.schoolId || !ctx.userId || !ctx.termId || !classroomId) return null;
  const key = [ctx.schoolId, ctx.userId, ctx.termId, classroomId];
  const existing = await db.preloadStatus.get(key);
  const next = {
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    sessionId: ctx.sessionId ?? existing?.sessionId ?? null,
    termId: ctx.termId,
    classroomId,
    status: patch.status ?? existing?.status ?? "idle",
    stale: patch.stale ?? existing?.stale ?? false,
    lastFetchedAt: patch.lastFetchedAt ?? existing?.lastFetchedAt ?? null,
    lastValidatedAt: patch.lastValidatedAt ?? existing?.lastValidatedAt ?? null,
    recordCount: patch.recordCount ?? existing?.recordCount ?? 0,
    commentCount: patch.commentCount ?? existing?.commentCount ?? 0,
    traitGradeCount: patch.traitGradeCount ?? existing?.traitGradeCount ?? 0,
    error: patch.error ?? existing?.error ?? null,
  };
  await db.preloadStatus.put(next);
  return next;
}

export async function getPreloadStatus(classroomId, state) {
  const ctx = getOfflineContext(state);
  if (!ctx.schoolId || !ctx.userId || !ctx.termId || !classroomId) return null;
  return db.preloadStatus.get([ctx.schoolId, ctx.userId, ctx.termId, classroomId]);
}

function isFresh(status) {
  return !!status?.lastFetchedAt && Date.now() - status.lastFetchedAt < PRELOAD_TTL_MS && !status?.stale;
}

async function writeBundleToDexie(classroomId, subjectIds, bundle, state) {
  const now = Date.now();
  const { term_id, session_id, students, assessments, records, comments, trait_grades, traits, grades } = bundle;
  setOfflineTermSession({ termId: term_id, sessionId: session_id });
  const ctx = getOfflineContext(state);
  const scoped = {
    schoolId: ctx.schoolId,
    userId: ctx.userId,
    sessionId: session_id,
    termId: term_id,
  };

  if (Array.isArray(students)) {
    await db.students.put({ ...scoped, classroomId, data: students, cachedAt: now });
  }

  if (Array.isArray(assessments)) {
    await db.assessments.put({ ...scoped, classroomId, data: assessments, cachedAt: now });
  }

  if (Array.isArray(records)) {
    const grouped = {};
    for (const id of subjectIds || []) grouped[id] = [];
    for (const row of records) {
      grouped[row.subject_id] = grouped[row.subject_id] || [];
      grouped[row.subject_id].push(row);
    }
    for (const [subjectId, rows] of Object.entries(grouped)) {
      await db.records.put({
        ...scoped,
        classroomId,
        subjectId: parseInt(subjectId),
        data: rows,
        cachedAt: now,
      });
    }
  }

  if (Array.isArray(comments)) {
    const bySubject = {};
    const byStudent = {};

    for (const row of comments) {
      const type = row.type || "term";
      if (row.subject_id) {
        const key = `${row.subject_id}:${type}`;
        bySubject[key] = bySubject[key] || [];
        bySubject[key].push(row);
      }

      const studentKey = JSON.stringify([
        row.student_id || 0,
        row.classroom_id || 0,
        row.section_id || 0,
        row.department_id || 0,
        row.subject_id ? `subject:${row.subject_id}:${type}` : type,
      ]);
      byStudent[studentKey] = byStudent[studentKey] || [];
      byStudent[studentKey].push(row);
    }

    for (const [key, rows] of Object.entries(bySubject)) {
      const [subjectId, type] = key.split(":");
      await db.comments.put({
        ...scoped,
        studentId: 0,
        classroomId,
        subjectId: parseInt(subjectId),
        sectionId: 0,
        departmentId: 0,
        type: `subject:${subjectId}:${type}`,
        data: rows,
        cachedAt: now,
      });
    }

    for (const [key, rows] of Object.entries(byStudent)) {
      const [studentId, classroomKey, sectionId, departmentId, type] = JSON.parse(key);
      await db.comments.put({
        ...scoped,
        studentId: parseInt(studentId),
        classroomId: parseInt(classroomKey),
        subjectId: 0,
        sectionId: parseInt(sectionId),
        departmentId: parseInt(departmentId),
        type,
        data: rows,
        cachedAt: now,
      });
    }
  }

  if (Array.isArray(trait_grades)) {
    const byStudent = trait_grades.reduce((acc, row) => {
      acc[row.student_id] = acc[row.student_id] || [];
      acc[row.student_id].push(row);
      return acc;
    }, {});
    for (const [studentId, rows] of Object.entries(byStudent)) {
      await db.traitGrades.put({
        ...scoped,
        classroomId,
        studentId: parseInt(studentId),
        data: rows,
        cachedAt: now,
      });
    }
  }

  if (Array.isArray(traits)) {
    const bySubsection = traits.reduce((acc, row) => {
      const subsectionId = row.subsection_id ?? 0;
      acc[subsectionId] = acc[subsectionId] || [];
      acc[subsectionId].push(row);
      return acc;
    }, {});
    for (const [subsectionId, rows] of Object.entries(bySubsection)) {
      await db.traits.put({
        schoolId: scoped.schoolId,
        userId: scoped.userId,
        subsectionId: parseInt(subsectionId),
        data: rows,
        cachedAt: now,
      });
    }
  }

  if (Array.isArray(grades)) {
    const grouped = grades.reduce((acc, row) => {
      const key = `${row.subsection_id}:${row.type}`;
      acc[key] = acc[key] || [];
      acc[key].push(row);
      return acc;
    }, {});
    for (const [key, rows] of Object.entries(grouped)) {
      const [subsectionId, type] = key.split(":");
      await db.grades.put({
        schoolId: scoped.schoolId,
        userId: scoped.userId,
        subsectionId: parseInt(subsectionId),
        type,
        data: rows,
        cachedAt: now,
      });
    }
  }

  await upsertPreloadStatus(scoped, classroomId, {
    status: "cached",
    stale: false,
    lastFetchedAt: now,
    lastValidatedAt: now,
    recordCount: Array.isArray(records) ? records.length : 0,
    commentCount: Array.isArray(comments) ? comments.length : 0,
    traitGradeCount: Array.isArray(trait_grades) ? trait_grades.length : 0,
    error: null,
  });
}

async function doPreloadClassroom(classroomId, subjectIds = [], state) {
  if (!classroomId || !navigator.onLine) return;
  updateOfflineContextFromAuth(state);
  const token = getToken();
  const ctx = getOfflineContext(state);
  if (!token || !ctx.schoolId || !ctx.userId) return;

  const dedupeKey = JSON.stringify([ctx.schoolId, ctx.userId, ctx.termId || 0, classroomId, [...subjectIds].sort()]);
  if (pendingPreloads.has(dedupeKey)) return pendingPreloads.get(dedupeKey);

  const run = (async () => {
    try {
      const status = await getPreloadStatus(classroomId, state);
      if (isFresh(status)) return;

      if (ctx.termId) {
        await upsertPreloadStatus(ctx, classroomId, {
          status: "syncing",
          stale: !!status?.lastFetchedAt,
          lastValidatedAt: Date.now(),
          error: null,
        });
      }

      let url = `${BASE_URL}/sync/preload?classroom_id=${classroomId}`;
      if (subjectIds?.length) {
        url += `&subject_ids=${subjectIds.join(",")}`;
      }

      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        await upsertPreloadStatus(ctx, classroomId, {
          status: "failed",
          stale: true,
          error: `Preload failed (${resp.status})`,
          lastValidatedAt: Date.now(),
        });
        return;
      }
      const bundle = await resp.json();
      await writeBundleToDexie(classroomId, subjectIds, bundle, state);
    } catch {
      await upsertPreloadStatus(ctx, classroomId, {
        status: "failed",
        stale: true,
        error: "Preload failed",
        lastValidatedAt: Date.now(),
      });
    } finally {
      pendingPreloads.delete(dedupeKey);
    }
  })();

  pendingPreloads.set(dedupeKey, run);
  return run;
}

export function preloadClassroom(classroomId, subjectIds = [], state) {
  schedule(() => {
    void doPreloadClassroom(classroomId, subjectIds, state);
  });
}
